import { Form, Formik, FormikHelpers } from 'formik';
import { useEffect } from 'react';
import { Flex, Skeleton } from '@chakra-ui/react';
import { validationResultUtils } from 'utils/validation';
import {
    TypedCheckboxFormikField,
    TypedComboboxFormikField,
    TypedInputFormikField,
    TypedTextareaFormikField,
} from 'types/utils';
import { StepDetails } from '../StepDetails';
import { FormattedMessage, useIntl } from 'react-intl';
import { PCW_STEPS } from '../constants';
import { useWizardState } from 'components/wizard/hooks/useWizardState';
import { useWizardContext } from 'components/wizard/hooks/useWizardContext';
import { useWizardNavigation } from 'components/wizard/hooks/useWizardNavigation';
import InfoPopover from 'components/forms/InfoPopover';
import { useGetMainMarketModels } from 'query/mainMarket';
import { useGetBooks } from 'query/books';
import { useWizardValidationResults } from 'components/wizard/hooks/useWizardValidationResults';

export interface PortfolioBasicsFormData {
    portfolioName: string;
    parentPortfolio: string;
    bookId: string;
    mainMarketModel: string;
    description?: string;
    isMaster: boolean;
}

const defaultValues: PortfolioBasicsFormData = {
    portfolioName: '',
    parentPortfolio: '',
    bookId: '',
    mainMarketModel: '',
    isMaster: false,
};

export function PortfolioBasicsStep() {
    const intl = useIntl();
    const { config } = useWizardContext();
    const { handleNext } = useWizardNavigation();
    const { formData, setMetadata, currentStep } = useWizardState();

    const { data: mainMarketModels, isLoading: isLoadingMainMarketModels } = useGetMainMarketModels();
    const { data: books, isLoading: isLoadingBooks } = useGetBooks();
    const { clearGlobalWarnings } = useWizardValidationResults();

    const initialValues: PortfolioBasicsFormData = {
        ...defaultValues,
        ...config?.steps[PCW_STEPS.PCW_PORTFOLIO_BASICS_STEP_ID]?.initialValues,
        ...formData[PCW_STEPS.PCW_PORTFOLIO_BASICS_STEP_ID],
    };

    const validateName = (value: string) => {
        const trimmed = value.trim();
        let errorMessage = '';

        if (trimmed.length === 0) {
            errorMessage = intl.formatMessage({ id: 'common_forms_validation_required' }, { label: 'Portfolio Name' });
        }

        return errorMessage;
    };

    const handleSubmit = async (values: PortfolioBasicsFormData, formik: FormikHelpers<PortfolioBasicsFormData>) => {
        const result = await handleNext(values);
        if (result) {
            validationResultUtils.setFormikStatusAndErrors(result, formik);
        }
    };

    useEffect(() => {
        setMetadata((prev: any) => ({ ...prev, books, mainMarketModels }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainMarketModels, books]);

    if (isLoadingMainMarketModels || isLoadingBooks) {
        return <Skeleton height="40px" />;
    }

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnChange={false}
            validateOnBlur={false}
            enableReinitialize
        >
            {() => (
                <>
                    <StepDetails />
                    <Form
                        id={`form-${currentStep.id}`}
                        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                    >
                        <TypedCheckboxFormikField id="isMaster" name="isMaster" mb={5}>
                            <Flex gap={2} alignItems={'center'}>
                                <FormattedMessage id="common_master" />
                                <InfoPopover info={<FormattedMessage id="wizard_pcw_basics_step_is_master_info" />} />
                            </Flex>
                        </TypedCheckboxFormikField>
                        <TypedInputFormikField
                            name="portfolioName"
                            validate={(value: string) => validateName(value)}
                            type="text"
                            label="Portfolio Name"
                            placeholder="Enter the portfolio name"
                            isRequired
                            mb={5}
                        />
                        <TypedInputFormikField
                            name="parentPortfolio"
                            type="text"
                            label="Parent Portfolio"
                            isDisabled
                            mb={5}
                        />
                        <TypedComboboxFormikField
                            id="bookId"
                            name="bookId"
                            label="Book ID"
                            options={books}
                            info={<FormattedMessage id="wizard_pcw_basics_step_bookid_info" />}
                            valueKey="id"
                            labelKey="name"
                            mb={5}
                        />
                        <TypedComboboxFormikField
                            id="mainMarketModel"
                            name="mainMarketModel"
                            label="Main Market Model"
                            options={mainMarketModels}
                            onChange={() => {
                                clearGlobalWarnings();
                            }}
                            info={<FormattedMessage id="wizard_pcw_basics_step_main_market_model_info" />}
                            valueKey="id"
                            labelKey="description"
                            isRequired
                            mb={5}
                        />
                        <TypedTextareaFormikField
                            name="description"
                            label="Description"
                            placeholder="Description"
                            mb={5}
                        />
                    </Form>
                </>
            )}
        </Formik>
    );
}
